import { memberRole, type CallChatMessage } from '@kanvas/board-core';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import { Icon } from '../ui/icons';
import { ModerationActions } from './ModerationActions';
import { useCall } from './CallProvider';
import { useFloatingWindow } from './useFloatingWindow';
import { usePopoutWindow } from './usePopoutWindow';
import { RoleBadge } from './RoleBadge';

/** Messages this close together from one person read as one turn of speech. */
const GROUP_WINDOW_MS = 4 * 60 * 1000;

const MIN_SIZE = { width: 280, height: 300 };

const REACTION_EMOJIS = ['👍', '❤️', '😂', '👀', '🔥'] as const;

function time(ts: number): string {
  return new Date(ts).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(file.name);
}

/**
 * The in-call chat.
 *
 * A window of its own rather than a panel bolted to the call: the two are
 * used together but sized differently — a chat wants to be tall and narrow,
 * a grid of faces wants to be wide — and on a shared screen the chat is
 * often the only one of the two worth keeping visible.
 */
export function CallChatWindow({ boardId }: { boardId: string }) {
  const call = useCall();

  const floating = useFloatingWindow(`kanvas.call-chat.${boardId}`, {
    defaultRect: ({ width, height }) => ({
      x: Math.max(12, width - 720),
      y: Math.max(12, height - 460),
      width: 340,
      height: 440,
    }),
    minWidth: MIN_SIZE.width,
    minHeight: MIN_SIZE.height,
  });

  const popout = usePopoutWindow({
    title: 'Чат звонка',
    name: 'kanvas-chat',
    width: 380,
    height: 540,
  });

  const closePopout = popout.close;
  useEffect(() => {
    if (!call.chatOpen) closePopout();
  }, [call.chatOpen, closePopout]);

  if (!call.chatOpen) return null;

  const popped = popout.open;

  const panel = (
    <section
      className={`chat-window ${popped ? 'is-popped' : ''} ${floating.dragging ? 'is-dragging' : ''}`}
      style={
        popped
          ? undefined
          : {
              left: floating.rect.x,
              top: floating.rect.y,
              width: floating.rect.width,
              height: floating.rect.height,
            }
      }
      aria-label="Чат звонка"
    >
      <header className="chat-bar" {...(popped ? {} : floating.dragHandlers)}>
        <span className="chat-bar-title">
          <Icon name="comment" size={14} />
          Чат звонка
        </span>
        <div className="chat-bar-actions">
          {popout.supported && (
            <button
              type="button"
              className="call-bar-btn"
              onClick={() => (popped ? popout.close() : popout.requestOpen())}
              aria-label={popped ? 'Вернуть чат в страницу' : 'Открыть чат отдельным окном'}
              data-tip={popped ? 'Вернуть в страницу' : 'Отдельное окно'}
            >
              <Icon name={popped ? 'popin' : 'popout'} size={15} />
            </button>
          )}
          <button
            type="button"
            className="call-bar-btn"
            onClick={call.closeChat}
            aria-label="Закрыть чат"
            data-tip="Закрыть чат"
          >
            <Icon name="close" size={15} />
          </button>
        </div>
      </header>

      <ChatLog messages={call.messages} />
      <ChatComposer
        onSend={call.sendChat}
        canWrite={call.canChat}
        chatMuted={call.chatMuted}
        chatMutedUntil={call.chatMutedUntil}
      />

      {!popped && <span className="window-resize" aria-hidden="true" {...floating.resizeHandlers} />}
    </section>
  );

  return popout.render(panel);
}

function ChatLog({ messages }: { messages: CallChatMessage[] }) {
  const ref = useRef<HTMLOListElement>(null);
  const pinned = useRef(true);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !pinned.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  if (messages.length === 0) {
    return (
      <div className="chat-empty">
        <Icon name="comment" size={20} />
        <p>Сообщений пока нет. Напишите первым — участники звонка увидят это сразу.</p>
      </div>
    );
  }

  return (
    <ol className="chat-log" ref={ref} onScroll={onScroll}>
      {messages.map((message, i) => {
        const previous = messages[i - 1];
        const grouped =
          previous?.userId === message.userId && message.ts - previous.ts < GROUP_WINDOW_MS;
        return <ChatMessage key={message.id} message={message} grouped={grouped} />;
      })}
    </ol>
  );
}

function ChatMessage({ message, grouped }: { message: CallChatMessage; grouped: boolean }) {
  const call = useCall();
  const [pickerOpen, setPickerOpen] = useState(false);
  const reactWrapRef = useRef<HTMLDivElement>(null);
  const role =
    message.roleSlug && message.roleLabel
      ? { slug: message.roleSlug, label: message.roleLabel }
      : memberRole(message.roleSlug);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (reactWrapRef.current?.contains(e.target as Node)) return;
      setPickerOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  const reactionEntries = Object.entries(message.reactions ?? {}).filter(([, ids]) => ids.length > 0);

  return (
    <li className={`chat-msg ${grouped ? 'is-grouped' : ''}`}>
      {!grouped ? (
        <div className="chat-msg-head">
          <span className="chat-msg-author" style={{ color: message.color }}>
            {message.name}
          </span>
          <RoleBadge role={role} small />
          <time className="chat-msg-time" dateTime={new Date(message.ts).toISOString()}>
            {time(message.ts)}
          </time>
          <ChatAuthorModMenu userId={message.userId} userName={message.name} />
        </div>
      ) : (
        <div className="chat-msg-grouped-mod">
          <ChatAuthorModMenu userId={message.userId} userName={message.name} />
        </div>
      )}
      {message.imageUrl && (
        <a
          className="chat-msg-image-wrap"
          href={message.imageUrl}
          target="_blank"
          rel="noreferrer"
        >
          <img className="chat-msg-image" src={message.imageUrl} alt="" loading="lazy" />
        </a>
      )}
      {message.text ? <p className="chat-msg-text">{message.text}</p> : null}

      <div className="chat-msg-footer">
        {reactionEntries.length > 0 && (
          <div className="chat-reactions" aria-label="Реакции">
            {reactionEntries.map(([emoji, ids]) => {
              const mine = ids.includes(call.myUserId);
              return (
                <button
                  key={emoji}
                  type="button"
                  className={`chat-reaction ${mine ? 'is-mine' : ''}`}
                  title={String(ids.length)}
                  onClick={() => call.reactToChat(message.id, emoji)}
                >
                  <span aria-hidden="true">{emoji}</span>
                  <span className="chat-reaction-count">{ids.length}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className="chat-react-wrap" ref={reactWrapRef}>
          <button
            type="button"
            className="chat-react-btn"
            aria-label="Добавить реакцию"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((v) => !v)}
          >
            +
          </button>
          {pickerOpen && (
            <div className="chat-react-picker" role="menu">
              {REACTION_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    call.reactToChat(message.id, emoji);
                    setPickerOpen(false);
                  }}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function ChatAuthorModMenu({ userId, userName }: { userId: string; userName: string }) {
  const call = useCall();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const place = () => {
      const rect = btnRef.current!.getBoundingClientRect();
      const panel = panelRef.current;
      const width = panel?.offsetWidth ?? 200;
      const height = panel?.offsetHeight ?? 280;
      let top = rect.bottom + 4;
      if (top + height > window.innerHeight - 8) top = rect.top - height - 4;
      let left = rect.right - width;
      left = Math.min(Math.max(8, left), window.innerWidth - width - 8);
      top = Math.min(Math.max(8, top), window.innerHeight - height - 8);
      setCoords({ top, left });
    };
    place();
    requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!call.canModerate || userId === call.myUserId) return null;

  const chatMuted = call.isUserChatMuted(userId);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="chat-mod-btn"
        aria-label={`Модерация: ${userName}`}
        aria-expanded={open}
        data-tip="Модерация"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <Icon name="more" size={13} />
      </button>
      {open &&
        createPortal(
          <div
            className="mod-menu-panel"
            role="menu"
            ref={panelRef}
            style={
              coords
                ? {
                    position: 'fixed',
                    top: coords.top,
                    left: coords.left,
                    right: 'auto',
                    bottom: 'auto',
                  }
                : { position: 'fixed', visibility: 'hidden' }
            }
          >
            <ModerationActions
              userId={userId}
              userName={userName}
              chatMuted={chatMuted}
              onMuteCall={(id) => void call.muteCallParticipant(id, true)}
              onUnmuteCall={(id) => void call.muteCallParticipant(id, false)}
              onMuteChat={(id, minutes) => void call.muteChat(id, minutes)}
              onUnmuteChat={(id) => void call.unmuteChat(id)}
              onKick={(id) => void call.kickCallParticipant(id)}
              onBan={(id) => void call.banFromCall(id)}
              onDone={() => setOpen(false)}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

function ChatComposer({
  onSend,
  canWrite,
  chatMuted,
  chatMutedUntil,
}: {
  onSend: (payload: { text?: string; imageUrl?: string | null; imageMime?: string | null }) => void;
  canWrite: boolean;
  chatMuted: boolean;
  chatMutedUntil: number | null;
}) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<{ url: string; mime: string; file?: File } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  const clearPreview = () => {
    if (preview?.url.startsWith('blob:')) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const attachFile = (file: File) => {
    if (!isImageFile(file)) return;
    if (preview?.url.startsWith('blob:')) URL.revokeObjectURL(preview.url);
    setPreview({ url: URL.createObjectURL(file), mime: file.type || 'image/png', file });
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed && !preview) return;
    setUploading(true);
    try {
      let imageUrl: string | null = null;
      let imageMime: string | null = null;
      if (preview?.file) {
        const asset = await api.uploadAsset(preview.file);
        imageUrl = asset.url;
        imageMime = asset.mime || preview.mime;
      } else if (preview) {
        imageUrl = preview.url;
        imageMime = preview.mime;
      }
      onSend({ text: trimmed, imageUrl, imageMime });
      setText('');
      clearPreview();
    } finally {
      setUploading(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          attachFile(file);
          return;
        }
      }
    }
    // Some browsers put screenshots only in files[]
    const files = e.clipboardData?.files;
    if (files?.length) {
      for (const file of files) {
        if (isImageFile(file)) {
          e.preventDefault();
          attachFile(file);
          return;
        }
      }
    }
  };

  if (!canWrite) {
    return (
      <p className="chat-readonly">
        {chatMuted
          ? chatMutedUntil
            ? `Вам временно запрещено писать в чат до ${new Date(chatMutedUntil).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}.`
            : 'Вам запрещено писать в чат.'
          : 'У вас доступ только для просмотра — писать в чат нельзя.'}
      </p>
    );
  }

  return (
    <form className="chat-composer" onSubmit={(e) => void submit(e)}>
      {preview && (
        <div className="chat-attach-preview">
          <img src={preview.url} alt="" />
          <button
            type="button"
            className="chat-attach-clear"
            aria-label="Убрать фото"
            onClick={clearPreview}
          >
            <Icon name="close" size={12} />
          </button>
        </div>
      )}
      <div className="chat-composer-row">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) attachFile(file);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          className="chat-attach-btn"
          aria-label="Прикрепить фото"
          data-tip="Фото"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <Icon name="paperclip" size={16} />
        </button>
        <textarea
          ref={areaRef}
          className="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder="Написать или вставить фото (Ctrl+V)…"
          rows={1}
          aria-label="Сообщение в чат звонка"
          disabled={uploading}
        />
        <button
          type="submit"
          className="chat-send"
          disabled={uploading || (!text.trim() && !preview)}
          aria-label="Отправить"
        >
          <Icon name="send" size={16} />
        </button>
      </div>
    </form>
  );
}
